import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconWand, IconClockBolt, IconUsers, IconChartBar, IconLifebuoy, IconApi,
  IconRefresh, IconCheck, IconPaperclip, IconSearch, IconSparkles,
} from '@tabler/icons-react';
import { IS_PRERENDER } from '@/lib/hydration';

/**
 * Hero product tour: a self-playing walkthrough of what Kora does (~38s, plays once, then offers Replay).
 * - Scenes are pure CSS animation keyed by scene index; only the scene clock is JS.
 * - The scene clock pauses on hover/focus, when off-screen or tab hidden, and via a visible pause button
 *   (the scene itself always finishes its entrance, so a paused scene is complete, never half-drawn).
 * - Prerender / first client render show scene 1 in its final state (hydration-safe).
 * - prefers-reduced-motion: no autoplay, no motion; the step buttons still work.
 */

/** Pace: every in-scene delay and animation is multiplied by K (also set as --k in CSS). */
const K = 1.5;
/** Per-scene dwell time (ms): scenes with more to read stay a little longer. */
const DWELL = [6600, 6000, 6200, 6400, 6600, 6000];

type Scene = { key: string; title: string; line: string; icon: typeof IconWand };
const SCENES: Scene[] = [
  { key: 'capture', title: 'Capture', line: 'Paste anything. AI turns it into a clean ticket, free.', icon: IconWand },
  { key: 'sla', title: 'SLAs and automation', line: 'Clocks, rules and alerts on every workspace, free.', icon: IconClockBolt },
  { key: 'team', title: 'Your whole team', line: 'Unlimited members. Claim, comment, @mention.', icon: IconUsers },
  { key: 'analyst', title: 'AI Analyst', line: 'Ask your tickets a question in plain English.', icon: IconChartBar },
  { key: 'portal', title: 'Portal and help center', line: 'Customers follow their own requests and find answers.', icon: IconLifebuoy },
  { key: 'api', title: 'Build on it', line: 'REST API and signed webhooks, 20+ endpoints.', icon: IconApi },
];

const d = (s: number) => ({ '--d': `${(s * K).toFixed(2)}s` } as React.CSSProperties);

function Words({ text, start = 0, step = 0.06 }: { text: string; start?: number; step?: number }) {
  return <>{text.split(' ').map((w, i) => <span key={i} className="ht-in" style={d(start + i * step)}>{w}{' '}</span>)}</>;
}

const Chip = ({ children, tone, delay = 0 }: { children: React.ReactNode; tone?: 'green' | 'amber' | 'red' | 'blue'; delay?: number }) => (
  <span className="ht-in ht-chip" style={{ ...d(delay), background: tone ? `var(--chip-${tone}-bg)` : 'var(--warm-card)', color: tone ? `var(--chip-${tone})` : 'var(--warm-muted)' }}>{children}</span>
);

const Card = ({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) => (
  <div className={`ht-in ht-card ${className}`} style={d(delay)}>{children}</div>
);

function SceneCapture() {
  return (
    <div className="ht-col">
      <Card delay={0.1}>
        <p className="ht-label">Slack · #support</p>
        <p className="ht-body"><Words text="hi — checkout keeps failing on mobile since this morning, order #4821. tried twice on wifi and on cellular. need this fixed today please" start={0.3} step={0.07} /></p>
      </Card>
      <div className="ht-in ht-flow" style={d(1.9)}><IconSparkles size={14} stroke={1.75} /> AI structuring · free</div>
      <Card delay={2.3}>
        <p className="ht-label">Ticket #4821</p>
        <p className="ht-title">Mobile checkout failing on Wi-Fi and cellular</p>
        <div className="ht-row">
          <Chip tone="blue" delay={2.6}>Payments</Chip><Chip tone="red" delay={2.8}>High</Chip><Chip delay={3.0}>Open</Chip><Chip tone="amber" delay={3.2}>SLA 1h</Chip>
        </div>
      </Card>
      <div className="ht-row ht-in" style={d(3.4)}>
        <span className="ht-pill">−1 credit · $0.10</span>
        <span className="ht-hint">1,100 → 1,099 credits</span>
      </div>
    </div>
  );
}

function SceneSla() {
  return (
    <div className="ht-col">
      <Card delay={0.1} className="ht-flex">
        <svg width="46" height="46" viewBox="0 0 48 48" aria-hidden="true">
          <circle cx="24" cy="24" r="20" fill="none" stroke="var(--warm-border)" strokeWidth="4" />
          <circle className="ht-ring" cx="24" cy="24" r="20" fill="none" stroke="var(--chip-green)" strokeWidth="4" strokeLinecap="round" strokeDasharray="126" transform="rotate(-90 24 24)" />
        </svg>
        <div><p className="ht-label">First response</p><p className="ht-title">59 min left</p></div>
        <Chip tone="green" delay={0.6}>Business hours</Chip>
      </Card>
      <Card delay={0.8}>
        <p className="ht-label">Automation rule</p>
        {[['When', 'a ticket is created', 1.3], ['If', 'category is Payments', 1.9], ['Then', 'priority → High, assign to Sarah', 2.5]].map(([k, v, t]) => (
          <div key={k as string} className="ht-rule ht-in" style={d(t as number)}>
            <b>{k}</b><span>{v}</span><IconCheck size={14} stroke={2.25} className="ht-ok" style={d((t as number) + 0.3)} />
          </div>
        ))}
      </Card>
      <div className="ht-row ht-in" style={d(3.1)}>
        <Chip tone="amber">Breach alert armed</Chip><Chip tone="green">Webhook ready</Chip><span className="ht-hint">Free on every workspace</span>
      </div>
    </div>
  );
}

function SceneTeam() {
  const people = [['S', 'Sarah', '#7C6F5B'], ['M', 'Mike', '#3A3630'], ['E', 'Emily', '#8A6B4A'], ['J', 'John', '#5B6F7C']];
  return (
    <div className="ht-col">
      <Card delay={0.1}>
        <div className="ht-flex">
          <div className="ht-avatars">
            {people.map(([i, n, c], k) => <span key={n} className="ht-in ht-avatar" style={{ ...d(0.3 + k * 0.25), background: c }} title={n}>{i}</span>)}
            <span className="ht-in ht-avatar ht-more" style={d(1.4)}>+12</span>
          </div>
          <Chip tone="green" delay={1.6}>Unlimited members · $0</Chip>
        </div>
      </Card>
      <Card delay={1.9}>
        <p className="ht-label">Ticket #4821 · claimed by Mike</p>
        <div className="ht-msg ht-in" style={d(2.2)}><span className="ht-avatar ht-sm" style={{ background: '#3A3630' }}>M</span><span>On it. Checking the payment logs.</span></div>
        <div className="ht-msg ht-in" style={d(2.9)}><span className="ht-avatar ht-sm" style={{ background: '#8A6B4A' }}>E</span><span><b className="ht-mention">@Sarah</b> can you check the refund?</span></div>
        <div className="ht-row ht-in" style={d(3.4)}><span className="ht-file"><IconPaperclip size={12} stroke={1.75} /> receipt-1047.pdf</span><Chip>Internal note</Chip></div>
      </Card>
    </div>
  );
}

function SceneAnalyst() {
  const bars: [string, number, number][] = [['Billing', 14, 0.2], ['Payments', 11, 0.2], ['Access', 7, 0.2], ['Other', 5, 0.2]];
  return (
    <div className="ht-col">
      <Card delay={0.1}>
        <p className="ht-label">Ask your tickets</p>
        <p className="ht-body"><Words text="How many open tickets by category this week?" start={0.3} step={0.12} /></p>
      </Card>
      <div className="ht-in ht-flow" style={d(1.8)}><IconSparkles size={14} stroke={1.75} /> Read-only query on your workspace</div>
      <Card delay={2.2}>
        <p className="ht-title">37 open tickets</p>
        {bars.map(([n, v], i) => (
          <div key={n} className="ht-bar">
            <span>{n}</span>
            <i><u className="ht-grow" style={{ ...d(2.5 + i * 0.2), width: `${(v / 14) * 100}%` }} /></i>
            <b>{v}</b>
          </div>
        ))}
      </Card>
      <div className="ht-row ht-in" style={d(3.5)}><span className="ht-pill">1 credit per answer</span><span className="ht-hint">Failed answers are free</span></div>
    </div>
  );
}

function ScenePortal() {
  return (
    <div className="ht-col">
      <Card delay={0.1}>
        <div className="ht-flex ht-between"><p className="ht-title" style={{ margin: 0 }}>Northwind Support</p><span className="ht-hint">+ New request</span></div>
        {[['Cannot download my invoice', 'Waiting on you', 'amber', 0.5], ['Update billing address', 'In progress', 'blue', 0.9], ['Add a teammate', 'Resolved', 'green', 1.3]].map(([t, s, tone, dl]) => (
          <div key={t as string} className="ht-item ht-in" style={d(dl as number)}><span>{t}</span><Chip tone={tone as 'amber'}>{s}</Chip></div>
        ))}
      </Card>
      <Card delay={1.9}>
        <div className="ht-search"><IconSearch size={14} stroke={1.75} /><Words text="how do I reset my password" start={2.1} step={0.1} /></div>
        {['Resetting your password', 'Updating your billing address'].map((a, i) => (
          <div key={a} className="ht-item ht-in" style={d(3.1 + i * 0.3)}><span>{a}</span><span className="ht-hint">Help center</span></div>
        ))}
      </Card>
    </div>
  );
}

function SceneApi() {
  const lines = ['POST /public/v1/tickets', '{ "title": "Checkout page crashing",', '  "category": "Payments",', '  "priority": "High" }'];
  return (
    <div className="ht-col">
      <div className="ht-in ht-code" style={d(0.1)}>
        {lines.map((l, i) => <div key={l} className="ht-in" style={d(0.4 + i * 0.35)}>{l}</div>)}
        <div className="ht-in ht-ok2" style={d(1.9)}>201 Created · ticket #4822</div>
      </div>
      <Card delay={2.3}>
        <p className="ht-label">Webhook · ticket.created</p>
        <div className="ht-item" style={{ border: 0, padding: 0 }}><span>Delivered to your endpoint</span><Chip tone="green">HMAC-signed</Chip></div>
      </Card>
      <div className="ht-row ht-in" style={d(3.0)}><Chip>20+ endpoints</Chip><Chip>Embedded sessions</Chip><span className="ht-hint">1 credit per 400 responses</span></div>
    </div>
  );
}

const VIEWS = [SceneCapture, SceneSla, SceneTeam, SceneAnalyst, ScenePortal, SceneApi];

export function HeroTour() {
  const [scene, setScene] = useState(0);
  const [hover, setHover] = useState(false);
  const [inView, setInView] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [finished, setFinished] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const remaining = useRef(DWELL[0]);
  const startedAt = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onMq = () => setReduced(mq.matches);
    mq.addEventListener('change', onMq);
    const onVis = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    let io: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== 'undefined' && root.current) {
      io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
      io.observe(root.current);
    }
    return () => { mq.removeEventListener('change', onMq); document.removeEventListener('visibilitychange', onVis); io?.disconnect(); };
  }, []);

  const running = !IS_PRERENDER && !reduced && !hover && inView && !hidden && !finished;

  useEffect(() => {
    if (!running) return;
    startedAt.current = Date.now();
    let fired = false;
    const t = window.setTimeout(() => {
      fired = true;
      if (scene === SCENES.length - 1) {
        setFinished(true);
      } else {
        remaining.current = DWELL[scene + 1];
        setScene(scene + 1);
      }
    }, remaining.current);
    return () => {
      window.clearTimeout(t);
      if (!fired) remaining.current = Math.max(250, remaining.current - (Date.now() - startedAt.current));
    };
  }, [running, scene]);

  /** Jump to a scene and play on from there. */
  const go = useCallback((i: number) => { remaining.current = DWELL[i]; setFinished(false); setScene(i); }, []);
  const replay = useCallback(() => go(0), [go]);
  const View = VIEWS[scene];
  const meta = SCENES[scene];
  const Icon = meta.icon;

  return (
    <div
      ref={root}
      className={`ht ${running ? '' : 'ht-paused'}`}
      style={{ '--k': K } as React.CSSProperties}
      role="group"
      aria-roledescription="carousel"
      aria-label="Kora product tour: capture, SLAs and automation, team, AI Analyst, portal and API"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
    >
      <div className="ht-win">
        <div className="ht-bar-top">
          <span className="ht-top-icon"><Icon size={16} stroke={1.5} /></span>
          <span className="ht-top-title">{meta.title}</span>
          <span className="ht-top-tag">{scene + 1} / {SCENES.length}</span>
        </div>
        <div className="ht-steps" role="tablist" aria-label="Tour scenes">
          {SCENES.map((s, i) => (
            <button key={s.key} type="button" role="tab" aria-selected={i === scene} aria-label={`${i + 1} of ${SCENES.length}: ${s.title}`} title={s.title} className="ht-step" onClick={() => go(i)}>
              <i className={i === scene ? 'on' : i < scene ? 'done' : ''} style={i === scene && !reduced ? ({ '--dur': `${DWELL[i]}ms` } as React.CSSProperties) : undefined} />
            </button>
          ))}
        </div>
        <div className="ht-stage" key={scene} aria-hidden="true"><View /></div>
        <div className="ht-foot">
          <p className="ht-caption" aria-live="polite">{meta.line}</p>
          <button type="button" className={`ht-replay ${finished ? 'show' : ''}`} onClick={replay} tabIndex={finished ? 0 : -1} aria-hidden={!finished} aria-label="Replay the tour">
            <IconRefresh size={14} stroke={1.75} /> Replay
          </button>
        </div>
      </div>
    </div>
  );
}
